// filepath: src/pages/DiagnosticsPage.jsx
// alertupdates Tier-6 — Diagnostics: per-camera health + log viewer WITHOUT
// SSH/journalctl. Detections & Alerts live on their own page (DetectionsPage).
import { useState, useEffect, useCallback, useMemo } from 'react';
import '../css/diagnostics.css';
import { apiFetch } from '../utils/streamApi';

const STATUS_COLORS = {
  GREEN: '#27ae60', YELLOW: '#f39c12', RED: '#e74c3c',
  DEAD: '#c0392b', INIT: '#7f8c8d',
};

// Status-shaded FPS sparkline — one bar per sample, colored by the same band the
// health dot uses. Dashed lines mark the YELLOW (0.8×exp) and RED (0.5×exp)
// thresholds so a camera hugging the boundary is obvious. `series` = [{ts,fps,status}].
// Hover shows a tooltip with the sample's time, fps, and status band.
function Sparkline({ series, exp }) {
  const [hover, setHover] = useState(null);   // { i, x, w }
  if (!series || series.length === 0) {
    return <div className="diag-spark-empty">collecting history&hellip;</div>;
  }
  const W = 240, H = 34, n = series.length;
  let max = Math.max(exp * 1.5, 1);
  for (const s of series) if (s.fps > max) max = s.fps;
  const yb = H - (0.8 * exp / max) * H;   // GREEN / YELLOW boundary
  const yr = H - (0.5 * exp / max) * H;   // YELLOW / RED boundary
  const bw = W / n;

  // viewBox is 240u wide but the svg is stretched to 100% (preserveAspectRatio
  // none), so map the mouse via the element's real pixel width, not viewBox units.
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - r.left;
    let i = Math.floor((px / r.width) * n);
    i = Math.max(0, Math.min(n - 1, i));
    setHover({ i, x: px, w: r.width });
  };

  const hv = hover ? series[hover.i] : null;
  const fmtTs = (ts) => ts
    ? new Date(ts * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    : '';
  const tipLeft = hover ? Math.max(28, Math.min(hover.x, hover.w - 28)) : 0;

  return (
    <div className="diag-spark-wrap" style={{ position: 'relative' }}>
      <svg className="diag-spark" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
           role="img" aria-label="FPS timeline"
           onMouseMove={onMove} onMouseLeave={() => setHover(null)}>
        <line x1="0" y1={yb} x2={W} y2={yb} stroke={STATUS_COLORS.YELLOW}
              strokeDasharray="3 3" opacity="0.4" />
        <line x1="0" y1={yr} x2={W} y2={yr} stroke={STATUS_COLORS.RED}
              strokeDasharray="3 3" opacity="0.4" />
        {series.map((s, i) => {
          const f = Number(s.fps) || 0;
          const y = H - (f / max) * H;
          return <rect key={i} x={(i * bw).toFixed(2)} y={y.toFixed(2)}
                       width={(bw + 0.6).toFixed(2)} height={(H - y).toFixed(2)}
                       fill={STATUS_COLORS[s.status] || '#888'}
                       opacity={hover && hover.i === i ? 1 : 0.9} />;
        })}
        {hover && (
          <line x1={(hover.i * bw + bw / 2).toFixed(2)} y1="0"
                x2={(hover.i * bw + bw / 2).toFixed(2)} y2={H}
                stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
        )}
      </svg>
      {hv && (
        <div className="diag-spark-tip" style={{ position: 'absolute', left: tipLeft, top: -6,
          transform: 'translate(-50%, -100%)', pointerEvents: 'none', whiteSpace: 'nowrap',
          background: 'rgba(20,20,20,0.95)', color: '#fff', fontSize: 11, lineHeight: 1.35,
          padding: '4px 7px', borderRadius: 4, boxShadow: '0 2px 8px rgba(0,0,0,0.4)', zIndex: 5 }}>
          <span style={{ color: STATUS_COLORS[hv.status] || '#fff', fontWeight: 700 }}>
            {(Number(hv.fps) || 0).toFixed(1)} fps
          </span>
          <span style={{ opacity: .7 }}> · {hv.status}</span>
          {hv.ts ? <div style={{ opacity: .6 }}>{fmtTs(hv.ts)}</div> : null}
        </div>
      )}
    </div>
  );
}

const TAGS = ['', 'YOLO', 'ALERT', 'RULE', 'GATE', 'CLOTHING', 'WATCHDOG', 'Dispatch'];

async function getJson(path) {
  const res = await apiFetch(path);
  try { return await res.json(); } catch { return null; }
}

function todayStr() {
  const d  = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;   // YYYY-MM-DD for <input type=date>
}

function daysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Days from → to inclusive, as YYYY-MM-DD, at most MAX_DAYS (PRVSK-126). Built from
// the date parts at local noon so a DST change can't skip or repeat a day.
const MAX_DAYS = 7;
function daysInRange(from, to) {
  const out = [];
  const [y, m, d] = from.split('-').map(Number);
  const cur = new Date(y, m - 1, d, 12);
  while (out.length < MAX_DAYS) {
    const s = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    out.push(s);
    if (s >= to) break;
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

// The log for a date range: one /logs/tail call per day (the box keeps one file per
// day). The From time applies to the first day only and the To time to the last, so
// "yesterday 22:00 → today 06:00" reads as one night. Log lines carry no date, so
// each day gets a divider when more than one is shown.
async function fetchLogRange({ date, toDate, camFilter, tagFilter, fromT, toT, lines }) {
  const days = daysInRange(date, toDate && toDate > date ? toDate : date);
  const out = []; let error = '';
  for (let i = 0; i < days.length; i++) {
    const q = new URLSearchParams({ lines: String(lines) });
    q.set('date', days[i].replace(/-/g, ''));   // today = live log; past = rotated file
    if (camFilter) q.set('cam', camFilter);
    if (tagFilter) q.set('tag', tagFilter);
    if (fromT && i === 0)               q.set('start', fromT);
    if (toT   && i === days.length - 1) q.set('end', toT);
    const d = await getJson(`/logs/tail?${q.toString()}`);
    if (days.length > 1) out.push(`──── ${days[i]} ────`);
    out.push(...(d?.lines || []));
    if (d?.error && days.length === 1) error = d.error;
  }
  return { lines: out, error };
}

function downloadFile(name, text, mime = 'text/plain') {
  // Android WebView can't hand blob: URLs to the native download path
  // (net::ERR_FILE_NOT_FOUND) — hand the bytes to the app shell instead.
  if (window.FlutterDownload) {
    const bytes = new TextEncoder().encode(text);
    let binary = '';
    const chunk = 0x8000;
    for (let i = 0; i < bytes.length; i += chunk) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
    }
    window.FlutterDownload.postMessage(JSON.stringify({ filename: name, mime, data: btoa(binary) }));
    return;
  }
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const a = document.createElement('a');
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function DiagnosticsPage() {
  const [health, setHealth]       = useState([]);
  const [expFps, setExpFps]       = useState(1.5);
  const [history, setHistory]     = useState([]);   // FPS timeline samples
  const [logs, setLogs]           = useState([]);
  const [camFilter, setCamFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const [search, setSearch]       = useState('');   // client-side text filter over loaded lines
  const [date, setDate]           = useState(todayStr);   // log date (YYYY-MM-DD)
  // A second date shows only after "+ range" — almost everyone looks at one day.
  const [rangeOn, setRangeOn]     = useState(false);
  const [rangeTo, setRangeTo]     = useState(todayStr);   // range end while rangeOn
  const toDate = rangeOn ? rangeTo : date;
  const [fromT, setFromT]         = useState('');          // HH:MM time window start
  const [toT, setToT]             = useState('');          // HH:MM time window end
  const [err, setErr]             = useState('');
  const [loadingLogs, setLoading] = useState(false);

  const camNames = health.map(c => c.name);

  const loadHealth = useCallback(async () => {
    try {
      const d = await getJson('/health/cameras');
      if (d?.cameras) { setHealth(d.cameras); setExpFps(d.expected_fps ?? 1.5); }
    } catch { /* keep last good */ }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const d = await getJson('/health/cameras/history?mins=30');
      if (d?.samples) setHistory(d.samples);
    } catch { /* keep last good */ }
  }, []);

  // Per-camera FPS series (idx → [{ts,fps,status}]) built from the timeline samples.
  const seriesByIdx = useMemo(() => {
    const map = {};
    for (const rec of history) {
      const cams = rec.cams || {};
      for (const id in cams) {
        (map[id] = map[id] || []).push({
          ts: rec.ts, fps: cams[id].fps, status: cams[id].status || 'DEAD',
        });
      }
    }
    return map;
  }, [history]);

  const loadLogs = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const d = await fetchLogRange({ date, toDate, camFilter, tagFilter, fromT, toT, lines: 400 });
      setLogs(d.lines);
      if (d.error) setErr(d.error);
    } catch (e) { setErr(e.message || 'Failed to load logs'); }
    finally { setLoading(false); }
  }, [camFilter, tagFilter, date, toDate, fromT, toT]);

  const downloadLogs = useCallback(async () => {
    try {
      const d = await fetchLogRange({ date, toDate, camFilter, tagFilter, fromT, toT, lines: 5000 });
      const span = toDate && toDate > date ? `${date}_to_${toDate}` : date;
      const tag = [span, camFilter, tagFilter].filter(Boolean).join('_') || span;
      downloadFile(`logs_${tag}.txt`, d.lines.join('\n'));
    } catch (e) { setErr(e.message || 'Download failed'); }
  }, [date, toDate, camFilter, tagFilter, fromT, toT]);

  // Health: initial + 10s auto-refresh. Timeline: 30s (matches the sampler cadence).
  // Both intervals clear on unmount, so refresh only runs while this page is open.
  useEffect(() => {
    loadHealth();
    const t = setInterval(loadHealth, 10000);
    return () => clearInterval(t);
  }, [loadHealth]);

  useEffect(() => {
    loadHistory();
    const t = setInterval(loadHistory, 30000);
    return () => clearInterval(t);
  }, [loadHistory]);

  // Logs: reload when any filter changes.
  useEffect(() => { loadLogs(); }, [loadLogs]);

  return (
    <div className="diag-page">
      <h1 className="diag-title">Diagnostics</h1>
      {err && <div className="diag-error">{err}</div>}

      {/* Per-camera health */}
      <section className="diag-card">
        <div className="diag-card-head">
          <h2>Camera Health <span className="diag-sub">(expected &ge; {expFps} fps)</span></h2>
          <button className="diag-btn" onClick={loadHealth}>&#8635; Refresh</button>
        </div>
        <div className="diag-health-grid">
          {health.length === 0 && <div className="diag-empty">No camera data yet&hellip;</div>}
          {health.map(c => (
            <div key={c.idx} className="diag-cam">
              <div className="diag-cam-top">
                <span className="diag-dot"
                      style={{ background: STATUS_COLORS[c.status] || '#888' }} />
                <div>
                  <div className="diag-cam-name">{c.name}</div>
                  <div className="diag-cam-meta">
                    {c.status} &middot; {c.fps == null ? '—' : `${c.fps} fps`}
                    {c.connected ? '' : ' · offline'}
                  </div>
                </div>
              </div>
              <Sparkline series={seriesByIdx[c.idx]} exp={expFps} />
            </div>
          ))}
        </div>
      </section>

      {/* Log tail */}
      <section className="diag-card">
        <div className="diag-card-head">
          <h2>Logs</h2>
          <div className="diag-filters">
            <select value={camFilter} onChange={e => setCamFilter(e.target.value)}>
              <option value="">All cameras</option>
              {camNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={tagFilter} onChange={e => setTagFilter(e.target.value)}>
              {TAGS.map(t => <option key={t} value={t}>{t || 'All tags'}</option>)}
            </select>
            {/* One day: [date] + range [from] [to]. A range pairs each time with its
                date — [date from] → [date to] — since From applies to the first day
                only and To to the last; side by side they read as one night. */}
            <span className="diag-dt-pair">
              <input type="date" className="diag-date" value={date} max={todayStr()}
                     title={rangeOn ? 'Start date' : 'Date'}
                     onChange={e => {
                       const v = e.target.value || todayStr();
                       setDate(v);
                       // Keep the range valid: the end can't be before the start.
                       if (rangeTo < v) setRangeTo(v);
                     }} />
              {rangeOn && (
                <input type="time" className="diag-date" value={fromT} title="Start time (on the start date)"
                       onChange={e => setFromT(e.target.value)} />
              )}
            </span>
            {rangeOn ? (
              <>
                <span className="diag-range-sep" aria-hidden="true">→</span>
                <span className="diag-dt-pair">
                  <input type="date" className="diag-date" value={rangeTo} min={date} max={todayStr()}
                         title={`End date (up to ${MAX_DAYS} days)`}
                         onChange={e => setRangeTo(e.target.value && e.target.value >= date ? e.target.value : date)} />
                  <input type="time" className="diag-date" value={toT} title="End time (on the end date)"
                         onChange={e => setToT(e.target.value)} />
                </span>
                <button type="button" className="diag-link" title="Back to one day"
                        onClick={() => setRangeOn(false)}>✕</button>
              </>
            ) : (
              <>
                <button type="button" className="diag-link" title="See logs across several days, e.g. one night"
                        onClick={() => {
                          // Opening a range on today's date starts it at yesterday — the
                          // usual reason is one night (e.g. 22:00 yesterday → 06:00 today).
                          if (date === todayStr()) setDate(daysAgo(1));
                          setRangeTo(todayStr());
                          setRangeOn(true);
                        }}>
                  + range
                </button>
                <input type="time" className="diag-date" value={fromT} title="From time"
                       onChange={e => setFromT(e.target.value)} />
                <input type="time" className="diag-date" value={toT} title="To time"
                       onChange={e => setToT(e.target.value)} />
              </>
            )}
            {/* Search + actions stay together when the row wraps. */}
            <span className="diag-actions">
              <input type="search" className="diag-date" style={{ flex: '1 1 120px', minWidth: 90 }}
                     placeholder="🔍 search text (e.g. box, pathway, crossed)"
                     value={search} onChange={e => setSearch(e.target.value)} />
              <button className="diag-btn" onClick={loadLogs}>{loadingLogs ? '…' : '↻'}</button>
              <button className="diag-btn" onClick={downloadLogs}>⬇ .txt</button>
            </span>
          </div>
        </div>
        <pre className="diag-logs">
          {(() => {
            const q = search.trim().toLowerCase();
            const shown = q ? logs.filter(l => l.toLowerCase().includes(q)) : logs;
            if (logs.length === 0) return 'No matching log lines.';
            if (shown.length === 0) return `No lines contain “${search}”.`;
            return shown.join('\n');
          })()}
        </pre>
      </section>
    </div>
  );
}
